'use strict';

const Sizzle = require('sizzle');
const extend = require('./extend');
const device = require('./device');
const Debug = require('../util/debug')('svp-domutils');

import { createEventWrapper } from './event-wrapper-api';
import { localStorage } from './storage';

const NAMESPACE = 'svp-domutils';
const STORAGE_NAMESPACE = 'svp_storage';

const scripts = {};

const keyCodeKeyMap = {
    9:	'tab',
    13: 'enter',
    17: 'controlleft',
    27: 'escape',
    32: 'space',
    35: 'end',
    36: 'home',
    37: 'arrowleft',
    38: 'arrowup',
    39: 'arrowright',
    40: 'arrowdown',
    48: 'digit0',
    49: 'digit1',
    50: 'digit2',
    51: 'digit3',
    52: 'digit4',
    53: 'digit5',
    54: 'digit6',
    55: 'digit7',
    56: 'digit8',
    57: 'digit9',
    67: 'keyc',
    70: 'keyf',
    77: 'keym',
    69: 'keye'
};

// Internet explorer and Firefox has their own vision of which key maps to which keyEvent.key ..
const ieKeyMap = {
    0: 'digit0',
    1: 'digit1',
    2: 'digit2',
    3: 'digit3',
    4: 'digit4',
    5: 'digit5',
    6: 'digit6',
    7: 'digit7',
    8: 'digit8',
    9: 'digit9',
    c: 'keyc',
    f: 'keyf',
    m: 'keym',
    e: 'keye',
    ' ': 'space',
    spacebar: 'space',
    escape: 'escape',
    up: 'arrowup',
    down: 'arrowdown',
    left: 'arrowleft',
    right: 'arrowright'
};

function _getBoundedRegExp(string) {
    return new RegExp('\\s?' + string, 'gi');
}

function _applyAll(el, fn) {
    const elements = Array.isArray(el) ? el : [el];
    elements.forEach(fn);
}

function _isFullscreen() {
    return document.fullscreen || document.mozFullScreen || document.webkitIsFullScreen || document.msFullscreenElement;
}

export function createNode(type, attributes) {
    let node = document.createElement(type);

    if (attributes && typeof attributes === 'object') {
        for (let key in attributes) {
            if (Object.prototype.hasOwnProperty.call(attributes, key)) {
                node.setAttribute(key, attributes[key]);
            }
        }
    }

    return node;
}

export function getAttributes(el) {
    const namedNodeMap = el.attributes;
    let res = {},
        currItem;

    for (let i = 0; i < namedNodeMap.length; i++) {
        currItem = namedNodeMap.item(i);
        res[currItem.name] = currItem.value;
    }

    return res;
}

export function fromString(htmlString) {
    let el = document.createElement('div');
    el.innerHTML = htmlString.trim();

    return el.childNodes[0];
}

export function getScript(src) {
    // Exit early if script already loaded
    const loadedScript = scripts[src];
    if (loadedScript) {
        if (loadedScript.pendingPromise) {
            return loadedScript.pendingPromise;
        }

        if (loadedScript.error) {
            return Promise.reject(loadedScript.error);
        }

        return Promise.resolve();
    }

    let scriptNode = this.createNode('script', {
        type: 'text/javascript',
        src: src
    });

    const promise = new Promise((resolve, reject) => {
        scriptNode.onload = function () {
            scriptNode.onload = undefined;
            scripts[src].pendingPromise = false;
            resolve();
        };
        scriptNode.onerror = function (err) {
            scriptNode.onerror = undefined;
            scripts[src].pendingPromise = false;
            scripts[src].error = err;
            reject(err);
        };
        Sizzle('body')[0].appendChild(scriptNode);
    });

    scripts[src] = { pendingPromise: promise };

    return promise;
}

/**
 * Toggle the specified className on the given element or array of elements.
 *
 * If two arguments are given: Toggles the className for the given element or
 * elements. That is, if the class exists it will be removed, if not, it will
 * be added.
 * If three arguments are given: If force is true, the class will be added.
 * If force is false, the class will be removed.
 *
 * Aimed to be similar to HTML5 classList.toggle().
 *
 * @param {HTMLElement|HTMLElement[]} el - Element or array of elements that
 * will be affected.
 * @param {string} className - Class name to toggle or set.
 * @param {boolean} [force] - If specified and true, the class will be added.
 * If specified and false, the class will be removed.
 * @see https://dom.spec.whatwg.org/#dom-domtokenlist-toggle
 */
export function toggleClass(el, className, force) {
    const regexp = _getBoundedRegExp(className);
    _applyAll(el, (element) => {
        if (regexp.test(element.className)) {
            if (force === undefined || force === false) {
                element.className = element.className.replace(regexp, '');
            }
        } else {
            if (force === undefined || force === true) {
                element.className += ' ' + className;
            }
        }
    });
}

export function addClass(el, className) {
    const regexp = _getBoundedRegExp(className);
    _applyAll(el, function (element) {
        if (!regexp.test(element.className)) {
            el.className = (el.className + ' ' + className).trim();
        }
    });
}

export function removeClass(el, className) {
    className = Array.isArray(className) ? className : [className];

    className.forEach(function (currClass) {
        const regexp = _getBoundedRegExp(currClass);
        _applyAll(el, function (element) {
            if (element && element.className) {
                element.className = element.className.replace(regexp, '');
            }
        });
    });
}

export function hasClass(el, className) {
    const regexp = _getBoundedRegExp(className),
        element = Array.isArray(el) ? el[0] : el;

    return regexp.test(element.className);
}

export function hide(el) {
    _applyAll(el, function (element) {
        element.style.display = 'none';
    });
}

export function show(el) {
    _applyAll(el, function (element) {
        if (element.style.display) {
            element.style.display = '';
        } else {
            element.style.display = 'block';
        }
    });
}

export function forceRedraw(element) {
    const displayStyle = element.style.display;
    element.style.display = 'none';

    element.style.display = displayStyle;
}

export function addEvents(element, events, handler, namespace) {
    const eventsArray = events.split(/,\s*/);
    namespace = namespace || NAMESPACE;

    if (!element.addEventSVP) {
        extend(element, createEventWrapper());
    }

    for (let i = 0; i < eventsArray.length; i++) {
        element.addEventSVP(namespace + '.' + eventsArray[i], handler);
    }
}

export function removeEvents(element, events, namespace) {
    namespace = namespace || NAMESPACE;
    const eventsArray = events.split(/,\s*/);

    if (!element.removeEventSVP) {
        Debug.error('Attempting to remove event from element without eventWrapperApi');

        return;
    }

    for (let i = 0; i < eventsArray.length; i++) {
        element.removeEventSVP(namespace + '.' + eventsArray[i]);
    }
}

export function getKeyFromKeyEvent(e) {
    if (e.key) {
        return ieKeyMap[e.key.toLowerCase()] || e.key.toLowerCase();
    } else if (e.keyCode) {
        return keyCodeKeyMap[e.keyCode];
    }
}

export function setStoredData(key, data) {
    localStorage.setItem(`${STORAGE_NAMESPACE}.${key}`, data);
}

export function getStoredData(key) {
    return localStorage.getItem(`${STORAGE_NAMESPACE}.${key}`);
}

export function isFullscreen() {
    return _isFullscreen();
}

export function toggleVideoFullscreen(api) {
    this.enterVideoFullscreen(api) || this.exitVideoFullscreen(api);
}

export function exitVideoFullscreen(api) {
    if (!_isFullscreen()) {
        return false;
    }

    if (document.exitFullscreen) {
        document.exitFullscreen();
    } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
    } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
    }

    api.fire('togglefullscreen', false);

    return true;
}

export function enterVideoFullscreen(api) {
    if (_isFullscreen()) {
        return false;
    }

    const fullscreenElement = api.getFullscreenElement();

    if (device.isIOS()) {
        api.video().webkitEnterFullscreen();
    } else if (fullscreenElement.requestFullscreen) {
        fullscreenElement.requestFullscreen();
    } else if (fullscreenElement.msRequestFullscreen) {
        fullscreenElement.msRequestFullscreen();
    } else if (fullscreenElement.mozRequestFullScreen) {
        fullscreenElement.mozRequestFullScreen();
    } else if (fullscreenElement.webkitRequestFullscreen) {
        fullscreenElement.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
    }

    api.fire('togglefullscreen', true);

    return true;
}




// WEBPACK FOOTER //
// ./src/js/util/dom.js